"use strict";
/*jshint esversion: 11 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import moment from 'moment';
import JustValidate from 'just-validate';
import { startLoadingSpinner, stopLoadingSpinner } from "../../csf-ufc-pivotal/includes/utils";
import CsfMembreDispensesDataLayer, {
  DISPENSES_REASON_CODES
} from "./CsfMembreDispensesDataLayer";
import PivotalAttachmentsApi
  from "../../../js/csf/pivotal/PivotalAttachmentsApi";
import PivotalApiEnvironments
  from "../../../js/csf/pivotal/PivotalApiEnvironments";
import {convertBase64, resetErrMessages} from "../../csf-ufc-fournisseur/includes/utils";
import CsfMembreUtils from "../utils/CsfMembreUtils";

export default function CsfMembreDispenseModal({educationFileId, exemptionItem, dispenseDataService, apiUrl, apiEnv, accessToken, shouldEmptyModal, setShouldEmptyModal}) {
  const [isError, setIsError] = useState(false);
  const dismissButton = useRef();
  const [validator, setValidator] = useState(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  const modalRef = useRef();

  const pivotalAttachmentsApiRef = useRef();
  const dispenseDataServiceRef = useRef();
  const educationFileIdRef = useRef();
  const exemptionItemRef = useRef();


  // fields à gérer
  const ATTACHMENT_FIELDS = [
    "EM_Attachement",
    "EM_Attachement_2",
    "EM_Attachement_3",
    "EM_Attachement_4",
    "EM_Attachement_5"
  ];

  // liste des fichiers existants -- état initial : édition ou vide pour création
  const [attachments, setAttachments] = useState(() =>
    ATTACHMENT_FIELDS.reduce((acc, field) => ({
      ...acc,
      [field]: exemptionItem?.[field] || null,
    }), {})
  );
  const [newFiles, setNewFiles] = useState({});
  const newFilesRef = useRef({});
  const [deletedFiles, setDeletedFiles] = useState({});
  const deletedFilesRef = useRef({});

  // Premier ATTACHMENT_FIELDS dispo SANS pièce jointe
  const [firstEmptyField, setFirstEmptyField] = useState(null);


  // Ajout d’un fichier
  const handleFileChange = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      setAttachments(prev => ({ ...prev, [field]: file.name }));
      setNewFiles(prev => ({ ...prev, [field]: file }));
      newFilesRef.current = { ...newFilesRef.current, [field]: file }; // Ajout/ref synchro
      setDeletedFiles(prev => ({ ...prev, [field]: false }));
      deletedFilesRef.current = { ...deletedFilesRef.current, [field]: false };
    }
  };

  // Suppression d’une pièce jointe (existante ou neuve)
  const handleRemove = (field) => {
    setAttachments(prev => ({ ...prev, [field]: null }));
    setNewFiles(prev => ({ ...prev, [field]: null }));
    newFilesRef.current = { ...newFilesRef.current, [field]: null };
    setDeletedFiles(prev => ({ ...prev, [field]: true }));
    deletedFilesRef.current = { ...deletedFilesRef.current, [field]: true };
  };

  useEffect(() => {
    if (!pivotalAttachmentsApiRef.current) {
      pivotalAttachmentsApiRef.current = new PivotalAttachmentsApi(apiUrl, apiEnv, accessToken);
    }
  }, [apiUrl, apiEnv]);

  useEffect(() => {
    if (accessToken && pivotalAttachmentsApiRef.current) {
      pivotalAttachmentsApiRef.current.setAccessToken(accessToken);
    }
  }, [accessToken]);

  useEffect(() => {
    if (dispenseDataService) {
      dispenseDataServiceRef.current = dispenseDataService;
    }
  }, [dispenseDataService]);

  useEffect(() => {
    if (educationFileId) {
      educationFileIdRef.current = educationFileId;
    }
  }, [educationFileId]);

  useEffect(() => {
    if (exemptionItem) {
      exemptionItemRef.current = exemptionItem;
    }

    setStartDate(exemptionItemRef.current?.Start_Date ? moment(exemptionItemRef.current?.Start_Date, "YYYY-MM-DD").format("YYYY-MM-DD") : '');
    setEndDate(exemptionItemRef.current?.End_Date ? moment(exemptionItemRef.current?.End_Date, "YYYY-MM-DD").format("YYYY-MM-DD") : '');
    setReason(exemptionItemRef.current?.Reason);

    // réinitialiser les fichiers initiaux existants
    setAttachments(
      ATTACHMENT_FIELDS.reduce((acc, field) => ({
        ...acc,
        [field]: exemptionItemRef.current?.[field] || null,
      }), {})
    );

    // on réinitialise aussi newFiles, firstEmptyField, etc. ici
    setNewFiles({});
    newFilesRef.current = {};
    setDeletedFiles({});
    deletedFilesRef.current = {};

  }, [exemptionItem]);

  useEffect(() => {
    setIsError(false);
    resetErrMessages();

    if (shouldEmptyModal) {
      setShouldEmptyModal(false);
      setStartDate('');
      setEndDate('');
      setReason('');
      validator.refresh();

      setAttachments(
        ATTACHMENT_FIELDS.reduce((acc, field) => ({
          ...acc,
          [field]: null,
        }), {})
      );
      setNewFiles({});
      newFilesRef.current = {};
      setDeletedFiles({});
      deletedFilesRef.current = {};
    }
  }, [shouldEmptyModal]);

  useEffect(() => {
    const emptyField = ATTACHMENT_FIELDS.find(
      field => !attachments[field] && !newFiles[field]
    );
    setFirstEmptyField(emptyField ?? null);
  }, [attachments, newFiles]);

  useEffect(() => {
    // Initialisation au premier rendu du modal
    const validator = new JustValidate('#membreDispenseModal form');

    validator
      .addField('#dispenses-raison', [
        { rule: 'required', errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required') }
      ])
      .addField('#dispenses-start', [
        { rule: 'required', errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required') }
      ])
      .addField('#dispenses-end', [
        { rule: 'required', errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required') },
        {
          validator: (value, fields) => {
            const start = fields['#dispenses-start'].elem.value;
            const end = value;
            if (!start || !end) return false;
            const startMoment = moment(start, 'YYYY-MM-DD');
            const endMoment = moment(end, 'YYYY-MM-DD');
            // minimum 4 semaines entre start et end
            return endMoment.diff(startMoment, 'days') >= 28;
          },
          errorMessage: window.Drupal.t('membre.dispense.dates.minIntervalError')
        }
    ]);
    setValidator(validator);

    // Listeners pour succès/échec
    validator.onSuccess(async (event) => {
      console.log('ici?')
      // Success: envoyer à l’API
      startLoadingSpinner();

      // upload document if present
      const fileUploads = [];
      Object.entries(newFilesRef.current).forEach(([fieldName, file]) => {
        if (file) {
          // handle added or modified files
          const extension = file.name.split('.').pop();
          const nameWithoutExtension = file.name.slice(0, -(extension.length + 1));

          const lastModifiedDateTime = new Date(file.lastModifiedDate);
          const lastModifiedDate = `${lastModifiedDateTime.getMonth() + 1}/${lastModifiedDateTime.getDate()}/${lastModifiedDateTime.getFullYear()}`;
          const lastModifiedTime = `${lastModifiedDateTime.getHours()}:${lastModifiedDateTime.getMinutes()}:${lastModifiedDateTime.getSeconds()}`;

          fileUploads.push({
            file: file,
            fileName: file.name,
            nameWithoutExtension: nameWithoutExtension,
            extension: extension,
            size: file.size,
            fieldName: fieldName,
            lastModifiedDate: lastModifiedDate,
            lastModifiedTime: lastModifiedTime,
          });
        }
      });

      // Téléversement des fichiers en parallèle
      try {
        const uploadResults = await Promise.all(
          fileUploads.map(async fu => {
            // Conversion du fichier en base64 et appel de l’API de téléversement
            const fileBase64 = await convertBase64(fu.file);
            const requestData = {"uploadFileBase64": fileBase64};
            const response = await pivotalAttachmentsApiRef.current.callTaskUploadEndpoint(
              requestData,
              PivotalApiEnvironments.getEspaceMembreSuffix()
            );
            // Récupération de l’ID de l’upload retourné
            return {
              ...fu,
              uploadID: response.payload.uploadID,
            };
          })
        );

        // Get validated current values from form fields
        const validatedStartDate = document.querySelector('#dispenses-start').value;
        const validatedEndDate = document.querySelector('#dispenses-end').value;

        // get values from form and submit to API
        let formData = {
          Education_File_Id: educationFileIdRef.current,
          Reason: document.querySelector("#dispenses-raison").value,
          Start_Date: validatedStartDate + 'T00:00:00',
          End_Date: validatedEndDate + 'T00:00:00',
        };

        // On ajoute dynamiquement pour chaque fichier les attributs nécessaires :
        uploadResults.forEach(ur => {
          formData[`${ur.fieldName}`] = ur.fileName;
          formData[`${ur.fieldName}__UploadID`] = ur.uploadID;
          formData[`RN_${ur.fieldName}_ED`] = ur.lastModifiedDate;
          formData[`RN_${ur.fieldName}_ET`] = ur.lastModifiedTime;
          formData[`RN_${ur.fieldName}_EX`] = ur.extension;
          formData[`RN_${ur.fieldName}_FN`] = ur.nameWithoutExtension;
          formData[`RN_${ur.fieldName}_FZ`] = ur.size;
        });

        // ajouter les fichiers à retirer
        Object.entries(deletedFilesRef.current).forEach(([fieldName, toDelete]) => {
          if (toDelete) {
            formData[`${fieldName}`] = false;
          }
        });

        // REDU_Exemption_Id provided as 1st parameter if saving existing dispense, else null to create
        dispenseDataServiceRef.current.submitDispenseData(
          exemptionItemRef.current?.REDU_Exemption_Id,
          formData,
          () => {
            // on Success
            stopLoadingSpinner();
            dismissButton.current.click();
            $('#membreDispenseConfirmationModal').modal('show');
          },
          () => {
            stopLoadingSpinner();
            setIsError(true);
          }
        );

      }
      catch (error) {
        setIsError(true);
        console.error('Erreur lors du téléversement des fichiers :', error);
      }

    });
    validator.onFail((fields) => {
      setIsError(true);
    });

    // Nettoyage si besoin au unmout du composant
    return () => validator.destroy();
  }, []);


  function handleCreateEditDispense(e) {
    e.preventDefault();

    console.log('submit validation')

    // validate form when submitting
    setIsError(false);
    if (validator) {
      validator.validate();
    }
  }

  // Rendu dynamique du champ d'attachement
  function renderAttachmentField(field) {
    const filename = attachments[field];
    const isNew = Boolean(newFiles[field]);

    if (filename) {
      return (
        <div className="form-group file-row w-100" key={`${field}-modal`}>
          <div className="file-custom">
            <div className="selectedFile">
              <span className="visually-hidden">{window.Drupal.t('fournisseur.gestion.file.selected')} : </span>
              {isNew ? (
              <span className="name">
                {filename}
              </span>
              ) : (
                <a href="#"
                  className="name"
                  onClick={() =>
                    pivotalAttachmentsApiRef.current.downloadAttachment({
                        form: 'CSF_EM_Exemption_REST',
                        recordId: exemptionItem.REDU_Exemption_Id,
                        fieldName: field,
                        filename: filename,
                        action: PivotalAttachmentsApi.getNewTabAction()
                      },
                      PivotalApiEnvironments.getEspaceMembreSuffix()
                    )
                  }
                >
                  {filename}
                </a>
              )}
              <button
                className="removeFile"
                data-toggle="tooltip"
                title={`${window.Drupal.t('fournisseur.gestion.file.deselected')} ${filename}`}
                onClick={e => { e.preventDefault(); handleRemove(field); }}
              >
                <i className="icon icon-remove"></i>
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Montre input file **seulement pour le premier champ à remplir**
    if (firstEmptyField === field) {
      return (
        <div key={`${field}-modal`} className="form-group file-row">
          <div className="file-custom">
            <div className="toSelectFile">
              <input type="hidden" className="file-name" name="" />
              <input
                type="file"
                className="visually-hidden"
                id={`modal-dispense-document-${field}`}
                name={`modal-dispense-document-${field}`}
                accept=".doc,.docx,.pdf,.png,.jpg,.jpeg"
                onChange={e => handleFileChange(field, e)}
              />
              <span className="name">
                {window.Drupal.t('membre.dispense.type.input-file.label')}
              </span>

              <label htmlFor={`modal-dispense-document-${field}`}>
                {window.Drupal.t('fournisseur.gestion.file.btn')}
              </label>
            </div>
          </div>
        </div>
      );
    }
    // Rien sinon
    return null;
  }

  return (
    <div ref={modalRef} className="modal fade" id="membreDispenseModal" tabIndex="-1" role="dialog" aria-labelledby="membreDispenseModalTitle"
      aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <form className="modal-content" onSubmit={handleCreateEditDispense}>
          <div className="modal-header d-flex align-items-center">
            <h2 id="membreDispenseModalTitle">{window.Drupal.t('membre.dispense.modal.title')}</h2>
            <button type="button" className="close" data-dismiss="modal" ref={dismissButton} aria-label={window.Drupal.t('lang.modal.close')}>
              <i className="icon icon-csf-close-noborder"><span className="d-none">&nbsp;</span></i>
            </button>
          </div>

          <div className="modal-body">
            {isError === true && (
              <div className="alert alert-danger" role="alert"
                aria-live="polite">{window.Drupal.t('broker.manager.error.message')}</div>
            )}
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="dispenses-raison">{window.Drupal.t('membre.dispense.reason')}*</label>
                  <select id="dispenses-raison" value={reason} onChange={e => setReason(e.target.value)}>
                    <option value="" disabled>{window.Drupal.t('membre.dispense.reason.default')}</option>
                    {Object.values(DISPENSES_REASON_CODES).map((num) => (
                      <option key={'dispenses-raison-'+num} value={num}>
                        {CsfMembreDispensesDataLayer.getReasonDescriptionFromCode(num)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <div className="form-group date">
                  <label htmlFor="dispenses-start">{window.Drupal.t('Start date')}*</label>
                  <input
                    type="date"
                    id="dispenses-start"
                    name="dispenses-start"
                    value={startDate}
                    onChange={(event) => setStartDate(moment(event.target.value, "YYYY-MM-DD").format("YYYY-MM-DD"))}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group date">
                  <label htmlFor="dispenses-end">{window.Drupal.t('End date')}*</label>
                  <input
                    type="date"
                    id="dispenses-end"
                    name="dispenses-end"
                    value={endDate}
                    onChange={(event) => setEndDate(moment(event.target.value, "YYYY-MM-DD").format("YYYY-MM-DD"))}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {ATTACHMENT_FIELDS.map(renderAttachmentField)}
              </div>
            </div>

            <div className="d-flex align-items-center mt-3 mb-3">
              <img className="flex-shrink-0 mr-2" src="/themes/custom/tink/dist/assets/icon-membre-warning.svg" alt="" />
              <p className="larger-text m-0">{window.Drupal.t('membre.dispense.modal.dateWarning')}</p>
            </div>
          </div>

          <div className="modal-footer">
              <div className="space-between-wrapper">
                {!exemptionItem && (
                  <button type="button" className='button btn-outlined btn-larger' data-dismiss="modal">
                    {window.Drupal.t('fournisseur.back.btn')}
                  </button>
                )}
                <input className="btn-submit btn-pale btn-larger" type="submit" value={window.Drupal.t('fournisseur.gestion.submit.label')}/>
                { (exemptionItem && CsfMembreDispensesDataLayer.isDeletableDispense(exemptionItem.REDU_Chrono_Followup_Type_Id)) && (
                  <button type="button"
                          className='button btn-red btn-larger'
                          data-dismiss="modal"
                          data-toggle="modal"
                          data-target="#membreDispenseDeleteModal"
                  >{window.Drupal.t('delete.btn')}</button>
                )}
              </div>
            </div>
        </form>
      </div>
    </div>
  )
}

CsfMembreDispenseModal.propTypes = {
  educationFileId: PropTypes.string,
  exemptionItem: PropTypes.object,
  dispenseDataService: PropTypes.oneOfType([
    PropTypes.instanceOf(CsfMembreDispensesDataLayer),
    PropTypes.object, // éviter warning du mauvais type avant son initialisation
  ]),
  apiUrl: PropTypes.string,
  apiEnv: PropTypes.string,
  accessToken: PropTypes.string,
  shouldEmptyModal: PropTypes.bool,
  setShouldEmptyModal: PropTypes.func,
};

